import { useLocation, useHistory } from 'react-router';
import { useState, useEffect, useRef } from 'react';
import { parseTagIdsFromUrl } from '../../utils/url-params';
export type ResourceTypeFilter = 'client' | 'national' | 'licensed_care';

export interface BrowseFiltersState {
  tagGroupId: number | undefined;
  tagIds: number[];
  experienceType?: string;
  searchQuery?: string;
  resourceType?: ResourceTypeFilter;
}

const RESOURCE_TYPE_VALUES: ResourceTypeFilter[] = ['client', 'national', 'licensed_care'];

/**
 * Hook for managing browse page filters with URL synchronization.
 * Handles parsing and syncing tag groups, tag IDs, optional experience type, search query, and resource type.
 */
export const useBrowseFilters = (
  includeExperienceType = false,
  includeSearchQuery = false,
  includeResourceType = false,
) => {
  const location = useLocation();
  const history = useHistory();
  const isUpdatingFromUrl = useRef(false);

  // Parse query parameters
  const parseQueryParams = (): BrowseFiltersState => {
    const searchParams = new URLSearchParams(location.search);
    const tagGroupIdParam = searchParams.get('tag_groups');
    const tagIdsParam = searchParams.get('tags');
    const experienceTypeParam = includeExperienceType ? searchParams.get('experience_type') : undefined;
    const searchQueryParam = includeSearchQuery ? searchParams.get('search') : undefined;
    const resourceTypeParam = includeResourceType ? searchParams.get('resource_type') : undefined;

    const tagGroupId = tagGroupIdParam ? parseInt(tagGroupIdParam, 10) : undefined;
    const tagIds = parseTagIdsFromUrl(tagIdsParam);
    const experienceType =
      includeExperienceType && (experienceTypeParam === 'video' || experienceTypeParam === 'audio')
        ? experienceTypeParam
        : undefined;
    const searchQuery = searchQueryParam || undefined;
    const resourceType =
      includeResourceType && resourceTypeParam && RESOURCE_TYPE_VALUES.includes(resourceTypeParam as ResourceTypeFilter)
        ? (resourceTypeParam as ResourceTypeFilter)
        : undefined;

    return {
      tagGroupId: isNaN(tagGroupId!) ? undefined : tagGroupId,
      tagIds,
      experienceType,
      searchQuery,
      resourceType,
    };
  };

  const initialParams = parseQueryParams();
  const [selectedTagGroupId, setSelectedTagGroupId] = useState<number | undefined>(initialParams.tagGroupId);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(initialParams.tagIds);
  const [selectedExperienceType, setSelectedExperienceType] = useState<string | undefined>(
    initialParams.experienceType,
  );
  const [searchQuery, setSearchQuery] = useState<string>(initialParams.searchQuery || '');
  const [selectedResourceType, setSelectedResourceType] = useState<ResourceTypeFilter | undefined>(
    initialParams.resourceType,
  );

  // Sync state with URL when it changes externally (e.g., browser back/forward)
  useEffect(() => {
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
    }

    const params = parseQueryParams();
    if (params.tagGroupId !== selectedTagGroupId) {
      setSelectedTagGroupId(params.tagGroupId);
      setSelectedTagIds(params.tagIds);
    } else if (JSON.stringify([...params.tagIds].sort()) !== JSON.stringify([...selectedTagIds].sort())) {
      setSelectedTagIds(params.tagIds);
    }

    if (includeExperienceType && params.experienceType !== selectedExperienceType) {
      setSelectedExperienceType(params.experienceType);
    }

    if (includeSearchQuery && params.searchQuery !== searchQuery) {
      setSearchQuery(params.searchQuery || '');
    }

    if (includeResourceType && params.resourceType !== selectedResourceType) {
      setSelectedResourceType(params.resourceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Update URL query params when state changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (selectedTagGroupId) {
      searchParams.set('tag_groups', selectedTagGroupId.toString());
    } else {
      searchParams.delete('tag_groups');
    }
    if (selectedTagIds.length > 0) {
      searchParams.set('tags', [...selectedTagIds].sort((a, b) => a - b).join(','));
    } else {
      searchParams.delete('tags');
    }
    if (includeExperienceType) {
      if (selectedExperienceType) {
        searchParams.set('experience_type', selectedExperienceType);
      } else {
        searchParams.delete('experience_type');
      }
    }
    if (includeSearchQuery) {
      if (searchQuery.trim()) {
        searchParams.set('search', searchQuery.trim());
      } else {
        searchParams.delete('search');
      }
    }
    if (includeResourceType) {
      if (selectedResourceType) {
        searchParams.set('resource_type', selectedResourceType);
      } else {
        searchParams.delete('resource_type');
      }
    }
    isUpdatingFromUrl.current = true;
    history.replace({ ...location, search: searchParams.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagGroupId, selectedTagIds, selectedExperienceType, searchQuery, selectedResourceType]);

  // Clear subtopic filters when topic changes
  useEffect(() => {
    if (!selectedTagGroupId) {
      setSelectedTagIds([]);
    }
  }, [selectedTagGroupId]);

  return {
    selectedTagGroupId,
    setSelectedTagGroupId,
    selectedTagIds,
    setSelectedTagIds,
    selectedExperienceType,
    setSelectedExperienceType,
    searchQuery: includeSearchQuery ? searchQuery : undefined,
    setSearchQuery: includeSearchQuery ? setSearchQuery : undefined,
    selectedResourceType: includeResourceType ? selectedResourceType : undefined,
    setSelectedResourceType: includeResourceType ? setSelectedResourceType : undefined,
  };
};
